import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiLongestWord } from '../utils/api';
import SeoContent from '../components/SeoContent';
import DefinableWord from '../components/DefinableWord';
import PageSEO from '../components/PageSEO';

export default function LongestWordFinder() {
  const [letters, setLetters] = useState('');
  const [results, setResults] = useState(null);
  const [longest, setLongest] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  async function handleSolve(e) {
    e.preventDefault();
    if (!letters.trim()) return;
    setLoading(true); setError(''); setResults(null);
    try {
      const { data } = await apiLongestWord(letters);
      setResults(data.results);
      setLongest(data.longest || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
    } finally { setLoading(false); }
  }

  const grouped = results
    ? results.reduce((acc, r) => { (acc[r.word.length] = acc[r.word.length] || []).push(r); return acc; }, {})
    : {};

  return (
    <div className="game-page">
      <PageSEO page="longest-word" />
      <div className="container" style={{maxWidth:700}}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">📏</span>
          <h1>Longest Word Finder</h1>
          <p>Enter a set of letters and find the longest possible word you can make!</p>
        </div>

        <div className="solver-card">
          <form onSubmit={handleSolve}>
            <div className="mb-3">
              <label className="fw-bold mb-1">Enter Letters</label>
              <input
                className="big-input"
                value={letters}
                onChange={e => setLetters(e.target.value.replace(/[^a-zA-Z]/g, '').slice(0, 20))}
                placeholder="e.g. RSTLNEA"
                maxLength={20}
                autoFocus
              />
              <small className="text-muted">{letters.length} letters · finds the longest word you can make</small>
            </div>
            <button type="submit" className="solve-btn" disabled={loading || !letters}>
              {loading ? <><span className="spinner-border spinner-border-sm me-2"></span>Finding…</> : '📏 Find Longest Word'}
            </button>
          </form>
        </div>

        {error && <div className="alert alert-danger alert-custom">{error}</div>}

        {results !== null && (
          <div className="solver-card">
            {longest.length > 0 ? (
              <div className="text-center mb-4 p-4 rounded-3" style={{background:'linear-gradient(135deg,#7c3aed22,#4f46e522)'}}>
                <div className="text-muted fw-bold mb-1">🏆 Longest Word{longest.length > 1 ? 's' : ''} ({longest[0].word.length} letters)</div>
                <div className="d-flex flex-wrap justify-content-center gap-2">
                  {longest.map(r => (
                    <span key={r.word} style={{
                      fontSize:'2rem', fontWeight:800, letterSpacing:3,
                      color:'var(--primary)', fontFamily:'Fredoka One, cursive'
                    }}>{r.word.toUpperCase()}</span>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-muted text-center">No words found. Try more letters!</p>
            )}

            {results.length > 0 && (
              <>
                <p className="result-count">All <strong>{results.length}</strong> words from <strong>{letters.toUpperCase()}</strong></p>
                {Object.keys(grouped).sort((a,b) => b - a).map(len => (
                  <div key={len} className="mb-3">
                    <h6 className="fw-bold text-muted mb-2">
                      {len}-letter words ({grouped[len].length})
                      {parseInt(len) === (longest[0]?.word.length) && <span className="ms-2 badge" style={{background:'var(--primary)',color:'#fff'}}>Longest</span>}
                    </h6>
                    <div className="words-cloud">
                      {grouped[len].map(r => (
                        <DefinableWord key={r.word} word={r.word} className="result-badge"
                          style={{...(longest.some(l => l.word === r.word) ? {background:'var(--primary)',color:'#fff'} : {}), textDecoration:'none'}}>
                          {r.word.toUpperCase()}
                        </DefinableWord>
                      ))}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
        <SeoContent game="longest-word" />
      </div>
    </div>
  );
}
